import React, { useState, useEffect } from "react";
import CompanyLogo from "../assets/company.png";
import axios from 'axios';
import { BaseUrl } from "../App";
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';


const Company = () => {
  // State for form fields
  const [formData, setFormData] = useState({
    language: "English (United States)",
    timezone: "",
    companyName: "",
    email: "",
    countryCode: "+1",
    phone: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    country: ""
  });

  const [logo, setLogo] = useState(CompanyLogo);
  const [successMessage, setSuccessMessage] = useState("")
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({
    code: "+1",
    flag: "🇺🇸", // Default: USA
  });
  const [languages, setLanguages] = useState([]);
  const [timezones, setTimezones] = useState([]);

  const countryCodes = [
    { name: "United States", code: "+1", flag: "🇺🇸" },
    { name: "Canada", code: "+1", flag: "🇨🇦" },
    { name: "United Kingdom", code: "+44", flag: "🇬🇧" },
    { name: "India", code: "+91", flag: "🇮🇳" },
    { name: "Germany", code: "+49", flag: "🇩🇪" },
    { name: "France", code: "+33", flag: "🇫🇷" },
  ];

  useEffect(() => {
    fetchSettings();
    fetchLanguages();
  }, []);

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);

  const fetchSettings = async () => {
    setLoading(true);
      try {
          const response = await axios.post(`${BaseUrl}/backend/api/settings.php`, {
              action: "get_all_settings",
              api_key: "1" // Replace with your actual API key
          }, {
              headers: {
                  'Content-Type': 'application/json'
              }
          });
  
          if (response.data.status === "true" && response.data.statuscode === 200) {
              const settings = response.data.response;
              const updatedFormData = { ...formData };
              const fetchedTimezones = [];
  
              settings.forEach(setting => {
                  switch (setting.option_name) {
                      case "ct_language":
                          updatedFormData.language = setting.option_value;
                          break;
                      case "mg_timezone":
                          updatedFormData.timezone = setting.option_value;
                          fetchedTimezones.push(setting);
                          break;
                      case "ct_company_name":
                          updatedFormData.companyName = setting.option_value;
                          break;
                      case "ct_company_email":
                          updatedFormData.email = setting.option_value;
                          break;
                      case "ct_company_country_code":
                          updatedFormData.countryCode = setting.option_value;
                          setSelectedCountry(prevState => ({
                              ...prevState,
                              code: setting.option_value
                          }));
                          break;
                      case "ct_company_phone":
                          updatedFormData.phone = setting.option_value;
                          break;
                      case "ct_company_address":
                          updatedFormData.address = setting.option_value;
                          break;
                      case "ct_company_city":
                          updatedFormData.city = setting.option_value;
                          break;
                      case "ct_company_state":
                          updatedFormData.state = setting.option_value;
                          break;
                      case "ct_company_zip_code":
                          updatedFormData.postalCode = setting.option_value;
                          break;
                      case "ct_company_country":
                          updatedFormData.country = setting.option_value;
                          break;
                      case "ct_company_logo":
                          setLogo(setting.option_value);
                          break;
                  }
              });
  
              setFormData(updatedFormData);
              setTimezones(fetchedTimezones);
          }
      } catch (error) {
          console.error('Error fetching settings:', error);
      }
  };
  
  const fetchLanguages = async () => {
      try {
          const response = await axios.post(`${BaseUrl}/backend/api/settings.php`, {
              action: "get_all_languages",
              api_key: "1" // Replace with your actual API key
          }, {
              headers: {
                  'Content-Type': 'application/json'
              }
          });
  
          if (response.data.status === "true" && response.data.statuscode === 200) {
              setLanguages(response.data.data);
          } else {
              console.error('Error fetching languages:', response.data.response);
          }
      } catch (error) {
          console.error('Error fetching languages:', error);
      }finally {
        setLoading(false);
      }
  };
  

  // Generic input change handler
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [id]: value
    }));
  };

  const handleCountryCodeChange = (e) => {
    const selected = countryCodes.find((country) => country.code === e.target.value);
    if (selected) {
      setSelectedCountry(selected);
      setFormData(prevData => ({
        ...prevData,
        countryCode: selected.code
      }));
    }
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value;
    // Allow only numeric input and limit to 10 digits
    if (/^\d*$/.test(value) && value.length <= 10) {
      setFormData(prevData => ({
        ...prevData,
        phone: value
      }));
    }
  };

  // Handle the image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setLogo(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Reset form state
  const resetForm = () => {
    setFormData({
      language: "English (United States)",
      timezone: "",
      companyName: "",
      email: "",
      countryCode: "+1",
      phone: "",
      address: "",
      city: "",
      state: "",
      postalCode: "",
      country: ""
    });
    setLogo(CompanyLogo);
    setSelectedCountry({ code: "+1", flag: "🇺🇸" });
  };

  // Validate form before submission
  const validateForm = () => {
    const requiredFields = ['companyName', 'email', 'phone', 'address', 'city', 'state', 'postalCode', 'country'];
    for (let field of requiredFields) {
      if (!formData[field]) {
        alert(`Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }

    if (formData.phone.length < 10) {
      alert("Please enter a valid 10-digit phone number");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
      e.preventDefault();
  
      // Validate form
      if (!validateForm()) {
          return;
      }
  
      // Prepare the data to be sent to the API
      const settingsToUpdate = [
          { option_name: "language", option_value: formData.language },
          { option_name: "mg_timezone", option_value: formData.timezone },
          { option_name: "ct_company_name", option_value: formData.companyName },
          { option_name: "ct_company_email", option_value: formData.email },
          { option_name: "ct_company_country_code", option_value: formData.countryCode },
          { option_name: "ct_company_phone", option_value: formData.phone },
          { option_name: "ct_company_address", option_value: formData.address },
          { option_name: "ct_company_city", option_value: formData.city },
          { option_name: "ct_company_state", option_value: formData.state },
          { option_name: "ct_postal_code", option_value: formData.postalCode },
          { option_name: "ct_company_country", option_value: formData.country },
          { option_name: "ct_company_logo", option_value: logo },
      ];
  
      try {
          for (const setting of settingsToUpdate) {
            setLoading(true);
              const response = await axios.post(`${BaseUrl}/backend/api/settings.php`, {
                  action: "update_setting",
                  api_key: "1", // Replace with your actual API key
                  option_name: setting.option_name,
                  option_value: setting.option_value
              }, {
                  headers: {
                      'Content-Type': 'application/json'
                  }
              });
  
              if (response.data.status !== "true" || response.data.statuscode !== 200) {
                setSuccessMessage("Update Company Detail Successfully!")

                 console.error(`Failed to update setting: ${setting.option_name}`);
                  throw new Error(`Failed to update setting: ${setting.option_name}`);
              }
          }
  
          // Show success message
          alert("Company details saved successfully!");
  
          // Optionally, you can fetch the updated settings here
          fetchSettings();
      } catch (error) {
          console.error('Error updating settings:', error);
          // alert("Failed to save company details. Please try again.");
      }finally {
        setLoading(false);
      }
  };
  

  return (
    <div className="relative min-h-screen">
    {/* Loading Indicator */}
      {loading && (
           <div className="fixed inset-0 flex items-center justify-center z-50">
             <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
               <FadeLoader 
                 height={15}
                 width={5}
                 color="#ffffff"
                 ariaLabel="loading"
               />
               <h1 className="mt-1 text-lg text-white">Please wait...</h1>
             </div>
           </div>
         )}
         
    <div className="bg-gray-50 min-h-screen w-full">
           {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {/* Main container with increased width */}
      <div className="w-full max-w-7xl xl:max-w-full mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-4 sm:py-6 md:py-8 lg:pl-64">
        {/* Header with Save Button */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Company Details</h1>
          <button
            type="submit"
            className="w-full sm:w-auto py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
            onClick={handleSubmit}
          >
            Save Settings
          </button>
        </div>
        
        {/* Form with increased width */}
        <form className="bg-white shadow-md sm:shadow-lg rounded-lg overflow-hidden w-full" onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
            {/* Two-column grid with wider gaps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {/* Language */}
              <div className="col-span-1">
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                  Language
                </label>
                <div className="relative">
                  <select
                    id="language"
                    value={formData.language}
                    onChange={handleInputChange}
                    className="block w-full pl-3 pr-10 py-2 text-sm md:text-base border-2 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md appearance-none bg-white hover:border-indigo-300 transition-all duration-200"
                  >
                    {languages.map((lang) => (
                      <option key={lang.language} value={lang.language}>
                        {lang.language}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* TimeZone */}
              <div className="col-span-1">
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
                  TimeZone
                </label>
                <div className="relative">
                  <select
                    id="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className="block w-full pl-3 pr-10 py-2 text-sm md:text-base border-2 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md appearance-none bg-white hover:border-indigo-300 transition-all duration-200"
                  >
                    {timezones.map((timezone) => (
                      <option key={timezone.id} value={timezone.option_value}>
                        {timezone.option_value}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Extra column for larger screens */}
              <div className="col-span-1 hidden lg:block"></div>

              {/* Company Name - Full width on all screens */}
              <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  id="companyName"
                  type="text"
                  placeholder="My Company Name"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 hover:border-indigo-300 transition-all duration-200"
                />
              </div>

              {/* Email Address - Full width on all screens */}
              <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="companyemail@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 hover:border-indigo-300 transition-all duration-200"
                />
              </div>

              {/* Country Code */}
              <div className="col-span-1">
                <label htmlFor="countryCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Country Code
                </label>
                <div className="relative">
                  <select
                    id="countryCode"
                    value={selectedCountry.code}
                    onChange={handleCountryCodeChange}
                    className="block w-full pl-3 pr-10 py-2 text-sm md:text-base border-2 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md appearance-none bg-white hover:border-indigo-300 transition-all duration-200"
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name} ({country.code})
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="col-span-1">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  id="phone"
                  type="text"
                  placeholder="Phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className="mt-1 block w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 hover:border-indigo-300 transition-all duration-200"
                />
                {formData.phone.length > 0 && formData.phone.length < 10 && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">Phone number must be exactly 10 digits.</p>
                )}
              </div>

              {/* Extra column for larger screens */}
              <div className="col-span-1 hidden lg:block"></div>

              {/* Address - Full width on all screens */}
              <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  id="address"
                  placeholder="SUITE 5A-1204, 799 E DRAGRAM"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 block w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 hover:border-indigo-300 transition-all duration-200"
                />
              </div>

              {/* City */}
              <div className="col-span-1">
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  id="city"
                  type="text"
                  placeholder="TUCSON"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 hover:border-indigo-300 transition-all duration-200"
                />
              </div>

              {/* State */}
              <div className="col-span-1">
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  id="state"
                  type="text"
                  placeholder="AZ"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 hover:border-indigo-300 transition-all duration-200"
                />
              </div>

              {/* Extra column for larger screens */}
              <div className="col-span-1 hidden lg:block"></div>

              {/* Postal Code */}
              <div className="col-span-1">
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Postal Code
                </label>
                <input
                  id="postalCode"
                  type="text"
                  placeholder="85001"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 hover:border-indigo-300 transition-all duration-200"
                />
              </div>

              {/* Country */}
              <div className="col-span-1">
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  id="country"
                  type="text"
                  placeholder="USA"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 hover:border-indigo-300 transition-all duration-200"
                />
              </div>

              {/* Extra column for larger screens */}
              <div className="col-span-1 hidden lg:block"></div>

              {/* Company Logo - Full width with optimization for width */}
              <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                <label htmlFor="companyLogo" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Logo
                </label>
                <div
                  className="mt-1 flex justify-center px-4 sm:px-6 md:px-8 pt-4 pb-4 md:py-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:border-indigo-500 transition-all duration-200 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                  onClick={() => document.getElementById("logoUploadInput").click()}
                >
                  <div className="space-y-1 text-center">
                    <img
                      src={logo || "/placeholder.svg"}
                      alt="Company Logo"
                      className="mx-auto h-20 sm:h-24 md:h-28 w-auto object-contain"
                    />
                    <div className="flex flex-col sm:flex-row text-sm text-gray-600 justify-center items-center gap-1">
                      <label
                        htmlFor="logoUploadInput"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="logoUploadInput"
                          name="logoUploadInput"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save Setting Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6 md:mb-8 ">

            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 sm:px-5 md:px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
            >
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
   
  </div>
  )
}

export default Company;
